import { CvImportEvidenceNotice } from "../../../components/cv/CvImportEvidenceNotice";
import React, { useState } from "react";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import type { CvSectionId } from "@tabbio/types";
import { useCVData } from "../../../lib/cv-mock-data";
import { WebPageHeader } from "../../../components/common/WebPageHeader";
import { MaterialSymbol } from "../../../components/icons/MaterialSymbol";
import { buildCvRoute } from "../../../lib/cv-target-route";
import {
  getCvSectionItemCount,
  getHiddenCvSections,
  getVisibleCvSections,
  isCvSectionVisible,
  moveCvSection,
  setCvSectionVisibility,
} from "../../../lib/cv-section-config";

// ─── Section row ─────────────────────────────────────────────────────
// Gemini list-row card pattern (§7.4.2): flat tinted surface, 20px radius,
// no shadow, no border.
function SectionRow({
  icon,
  label,
  value,
  onClick,
}: {
  icon: string;
  label: string;
  value?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="tabbio-list-row"
      onClick={onClick}
      style={{
        width: "100%",
        border: 0,
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "16px 18px",
        cursor: "pointer",
        textAlign: "start",
        font: "inherit",
        color: "inherit",
      }}
    >
      <span aria-hidden className="tabbio-list-row-icon">
        <MaterialSymbol name={icon} size={24} />
      </span>
      <div className="tabbio-list-row-body">
        <div className="tabbio-list-row-title">{label}</div>
        {value ? <div className="tabbio-list-row-sub">{value}</div> : null}
      </div>
      <span className="tabbio-list-row-trailing">
        <MaterialSymbol name="chevron_right" size={22} />
      </span>
    </button>
  );
}

function ManageSectionRow({
  icon,
  label,
  value,
  position,
  visible,
  onToggleVisible,
  toggleDisabled,
  onMoveUp,
  onMoveDown,
  moveUpDisabled,
  moveDownDisabled,
  visibilityLabel,
  moveUpLabel,
  moveDownLabel,
}: {
  icon: string;
  label: string;
  value: string;
  position?: number;
  visible: boolean;
  onToggleVisible: () => void;
  toggleDisabled?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  moveUpDisabled?: boolean;
  moveDownDisabled?: boolean;
  visibilityLabel: string;
  moveUpLabel: string;
  moveDownLabel: string;
}) {
  const iconButton = (
    iconName: string,
    onClick: (() => void) | undefined,
    labelText: string,
    disabled?: boolean,
  ) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !onClick}
      aria-label={labelText}
      title={labelText}
      style={{
        width: 34,
        height: 34,
        borderRadius: 17,
        border: "1px solid var(--input-border)",
        background: "transparent",
        color: "var(--text-secondary)",
        cursor: disabled || !onClick ? "default" : "pointer",
        opacity: disabled || !onClick ? 0.35 : 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color 150ms, color 150ms",
      }}
    >
      <MaterialSymbol name={iconName} size={19} />
    </button>
  );

  return (
    <div
      className="tabbio-list-row"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "14px 16px",
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: 13,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          background: visible ? "var(--text-primary)" : "var(--alpha-05)",
          color: visible ? "var(--bg)" : "var(--text-tertiary)",
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {visible && position ? position : "–"}
      </span>
      <span aria-hidden className="tabbio-list-row-icon">
        <MaterialSymbol name={icon} size={23} />
      </span>
      <div className="tabbio-list-row-body">
        <div className="tabbio-list-row-title">{label}</div>
        <div className="tabbio-list-row-sub">{value}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {iconButton(
          visible ? "visibility" : "visibility_off",
          onToggleVisible,
          visibilityLabel,
          toggleDisabled,
        )}
        {visible ? (
          <>
            {iconButton(
              "keyboard_arrow_up",
              onMoveUp,
              moveUpLabel,
              moveUpDisabled,
            )}
            {iconButton(
              "keyboard_arrow_down",
              onMoveDown,
              moveDownLabel,
              moveDownDisabled,
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

// ─── Section group header ────────────────────────────────────────────
// Gemini §7.6: 18px / 500
function GroupHeader({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 18,
        fontWeight: 500,
        color: "var(--text-primary)",
        padding: "0 8px",
        marginTop: 32,
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  );
}

// ─── Screen ──────────────────────────────────────────────────────────

export default function EditSectionsWebScreen() {
  const { t } = useTranslation("seeker");
  const router = useRouter();
  const { profile, target, isDelegated, sectionsConfig, updateSectionsConfig } =
    useCVData();
  const [manageMode, setManageMode] = useState(false);
  const visibleSections = getVisibleCvSections(sectionsConfig);
  const hiddenSections = getHiddenCvSections(sectionsConfig);

  const nav = (route: string) =>
    router.push(buildCvRoute(`/(app)/cv/${route}`, target));

  const setSectionVisible = (sectionId: CvSectionId, visible: boolean) => {
    updateSectionsConfig(
      setCvSectionVisibility(sectionsConfig, sectionId, visible),
    );
  };

  const moveSection = (sectionId: CvSectionId, direction: "up" | "down") => {
    updateSectionsConfig(moveCvSection(sectionsConfig, sectionId, direction));
  };

  const openSection = (sectionId: CvSectionId, route: string) => {
    if (!isCvSectionVisible(sectionsConfig, sectionId)) {
      setSectionVisible(sectionId, true);
    }
    nav(route);
  };

  const sectionCountText = (sectionId: CvSectionId) => {
    const count = getCvSectionItemCount(profile, sectionId);
    return sectionId === "summary"
      ? count > 0
        ? t("cv.edit.filled")
        : t("cv.edit.empty")
      : t("cv.edit.items", { count });
  };

  const sectionValue = (sectionId: CvSectionId) => {
    const countText = sectionCountText(sectionId);
    return isCvSectionVisible(sectionsConfig, sectionId)
      ? countText
      : `${t("cv.edit.sectionHidden")} · ${countText}`;
  };

  const sectionDefinitions: Array<{
    id: CvSectionId;
    icon: string;
    label: string;
    route: string;
  }> = [
    {
      id: "summary",
      icon: "description",
      label: t("cv.edit.summary"),
      route: "edit-summary",
    },
    {
      id: "careerHighlights",
      icon: "emoji_events",
      label: t("cv.edit.highlights"),
      route: "edit-highlights",
    },
    {
      id: "skills",
      icon: "auto_awesome",
      label: t("cv.edit.skills"),
      route: "edit-skills",
    },
    {
      id: "experience",
      icon: "work",
      label: t("cv.edit.experience"),
      route: "edit-experience",
    },
    {
      id: "projects",
      icon: "work",
      label: t("cv.edit.projects"),
      route: "edit-projects",
    },
    {
      id: "education",
      icon: "school",
      label: t("cv.edit.education"),
      route: "edit-education",
    },
    {
      id: "certifications",
      icon: "workspace_premium",
      label: t("cv.edit.certifications"),
      route: "edit-certifications",
    },
    {
      id: "languages",
      icon: "language",
      label: t("cv.edit.languages"),
      route: "edit-languages",
    },
  ];
  const sectionById = Object.fromEntries(
    sectionDefinitions.map((section) => [section.id, section]),
  ) as Record<CvSectionId, (typeof sectionDefinitions)[number]>;

  const renderManageRow = (
    sectionId: CvSectionId,
    index: number,
    allSections: CvSectionId[],
    visible: boolean,
  ) => {
    const section = sectionById[sectionId];
    return (
      <ManageSectionRow
        key={sectionId}
        icon={section.icon}
        label={section.label}
        value={sectionCountText(sectionId)}
        position={visible ? index + 1 : undefined}
        visible={visible}
        onToggleVisible={() => setSectionVisible(sectionId, !visible)}
        toggleDisabled={visible && visibleSections.length <= 1}
        onMoveUp={() => moveSection(sectionId, "up")}
        onMoveDown={() => moveSection(sectionId, "down")}
        moveUpDisabled={index === 0}
        moveDownDisabled={index === allSections.length - 1}
        visibilityLabel={
          visible ? t("cv.edit.hideSection") : t("cv.edit.showSection")
        }
        moveUpLabel={t("cv.edit.moveSectionUp")}
        moveDownLabel={t("cv.edit.moveSectionDown")}
      />
    );
  };

  const manageButton = (
    <button
      type="button"
      onClick={() => setManageMode((current) => !current)}
      style={{
        height: 36,
        paddingInline: 14,
        borderRadius: 9999,
        border: "1px solid var(--input-border)",
        background: manageMode ? "var(--text-primary)" : "var(--bg)",
        color: manageMode ? "var(--bg)" : "var(--text-primary)",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        font: "inherit",
        fontSize: 13,
        fontWeight: 600,
        transition: "background-color 150ms, color 150ms",
      }}
    >
      <MaterialSymbol name={manageMode ? "check" : "tune"} size={18} />
      {manageMode ? t("cv.edit.doneManaging") : t("cv.edit.manageSections")}
    </button>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "var(--bg)",
      }}
    >
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div
          className="tabbio-phone-edit-page"
          style={{
            maxWidth: 820,
            width: "100%",
            marginInline: "auto",
            padding: "64px 32px 32px",
          }}
        >
          <CvImportEvidenceNotice cvId={target} enabled={!isDelegated} />
          <WebPageHeader
            title={t("cv.edit.sections")}
            onBack={() => router.back()}
          >
            {manageButton}
          </WebPageHeader>

          {manageMode ? (
            <>
              <GroupHeader>{t("cv.edit.shownSections")}</GroupHeader>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {visibleSections.map((sectionId, index, allSections) =>
                  renderManageRow(sectionId, index, allSections, true),
                )}
              </div>
              {hiddenSections.length > 0 ? (
                <>
                  <GroupHeader>{t("cv.edit.hiddenSections")}</GroupHeader>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {hiddenSections.map((sectionId, index, allSections) =>
                      renderManageRow(sectionId, index, allSections, false),
                    )}
                  </div>
                </>
              ) : null}
            </>
          ) : (
            <>
              {!isDelegated ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <SectionRow
                    icon="upload_file"
                    label={t("cv.resumeImport.updateEntireAction")}
                    value={t("cv.resumeImport.updateEntireActionHint")}
                    onClick={() => nav("import-resume")}
                  />
                </div>
              ) : null}

              {/* Personal Info */}
              <GroupHeader>{t("cv.edit.personalInfo")}</GroupHeader>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <SectionRow
                  icon="person"
                  label={t("cv.edit.headerContact")}
                  onClick={() => nav("edit-header")}
                />
                <SectionRow
                  icon="description"
                  label={t("cv.edit.summary")}
                  value={sectionValue("summary")}
                  onClick={() => openSection("summary", "edit-summary")}
                />
              </div>

              {/* Career */}
              <GroupHeader>{t("cv.edit.career")}</GroupHeader>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <SectionRow
                  icon="emoji_events"
                  label={t("cv.edit.highlights")}
                  value={sectionValue("careerHighlights")}
                  onClick={() =>
                    openSection("careerHighlights", "edit-highlights")
                  }
                />
                <SectionRow
                  icon="work"
                  label={t("cv.edit.experience")}
                  value={sectionValue("experience")}
                  onClick={() => openSection("experience", "edit-experience")}
                />
                <SectionRow
                  icon="work"
                  label={t("cv.edit.projects")}
                  value={sectionValue("projects")}
                  onClick={() => openSection("projects", "edit-projects")}
                />
                <SectionRow
                  icon="school"
                  label={t("cv.edit.education")}
                  value={sectionValue("education")}
                  onClick={() => openSection("education", "edit-education")}
                />
                <SectionRow
                  icon="workspace_premium"
                  label={t("cv.edit.certifications")}
                  value={sectionValue("certifications")}
                  onClick={() =>
                    openSection("certifications", "edit-certifications")
                  }
                />
              </div>

              {/* Skills & Languages */}
              <GroupHeader>{t("cv.edit.skillsAndLanguages")}</GroupHeader>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <SectionRow
                  icon="auto_awesome"
                  label={t("cv.edit.skills")}
                  value={sectionValue("skills")}
                  onClick={() => openSection("skills", "edit-skills")}
                />
                <SectionRow
                  icon="language"
                  label={t("cv.edit.languages")}
                  value={sectionValue("languages")}
                  onClick={() => openSection("languages", "edit-languages")}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
